import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Modal,
  useWindowDimensions,
  TextInput,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { formatPct } from '../../utils/formatPercentage';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import {
  PageContainer,
  Navbar,
  Breadcrumb,
  CommentSection,
  CommentData,
  getAvatarColorFromUserType,
  UserType,
  KPIDistributionCard,
} from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { unitGoalsApi, unitGoalCommentsApi, UnitGoalComment as ApiUnitGoalComment } from '../../services/apiService';
import { UnitGoal, KPIList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';

type RouteProps = RouteProp<MainStackParamList, 'UnitGoalDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MoreVerticalIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="2" fill={color} />
    <Circle cx="12" cy="12" r="2" fill={color} />
    <Circle cx="12" cy="19" r="2" fill={color} />
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Progress Component
// ============================================================

interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return '#22C55E';  // Green - Excellent
  if (percentage >= 80) return '#F97316';   // Orange - Good
  return '#EF4444';                          // Red - Needs Improvement
};

const CircularProgress = ({ percentage, size = 168, strokeWidth = 12 }: CircularProgressProps) => {
  const { isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(100, Math.max(0, percentage));
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = getAchievementColor(percentage);
  const bgColor = isDark ? '#374151' : '#E5E7EB';

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((progress / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={bgColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {progress > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={styles.progressTextContainer}>
        <Text style={[styles.progressPercentage, { color: isDark ? '#F9FAFB' : '#414042' }]}>%{formatPct(percentage)}</Text>
      </View>
    </View>
  );
};

// ============================================================
// KPI Card Component - Unified with ServiceDetailScreen
// ============================================================

interface KPICardProps {
  kpi: KPIList;
  onPress: () => void;
  cardWidth?: number;
}

const KPICard = ({ kpi, onPress, cardWidth }: KPICardProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate } = useAutoTranslate();
  const achievement = kpi.achievement_percentage || 0;
  const currentValue = kpi.latest_value || '0';
  const targetValue = kpi.target_value || '0';

  // Get color based on achievement percentage (uses global function)
  const getProgressColor = getAchievementColor;

  // Format value display - show percentage if it's a percentage-based KPI
  const isPercentage = targetValue === '100' || parseFloat(targetValue) === 100;
  const displayValue = isPercentage
    ? `% ${achievement}`
    : `${targetValue}\\${currentValue}`;

  const progressColor = getProgressColor(achievement);

  return (
    <View style={[styles.kpiCard, cardWidth && { width: cardWidth }, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* KPI Title */}
      <Text style={[styles.kpiCardTitle, { color: colors.textPrimary }]} numberOfLines={2}>
        {translate(kpi.name)}
      </Text>

      {/* Progress Bar Row */}
      <View style={styles.kpiCardStats}>
        {/* Value Badge */}
        <View style={[styles.kpiValueBadge, { backgroundColor: colors.backgroundAlt }]}>
          <Text style={[styles.kpiValueText, { color: colors.textPrimary }]}>{displayValue}</Text>
        </View>

        {/* Progress Bar with dot indicator */}
        <View style={styles.kpiCardProgress}>
          <View style={[styles.kpiProgressBarBg, { backgroundColor: isDark ? '#374151' : '#E5E7EB' }]}>
            <View
              style={[
                styles.kpiProgressBarFill,
                {
                  width: `${Math.min(achievement, 100)}%`,
                  backgroundColor: progressColor
                }
              ]}
            />
          </View>
          {/* Progress dot with border */}
          <View style={[styles.kpiProgressDotOuter, { borderColor: progressColor }]}>
            <View style={[styles.kpiProgressDotInner, { backgroundColor: progressColor }]} />
          </View>
        </View>
      </View>

      {/* View Details Link */}
      <TouchableOpacity style={styles.kpiCardLink} onPress={onPress}>
        <ChevronLeftIcon size={14} color={colors.textPrimary} />
        <Text style={[styles.kpiCardLinkText, { color: colors.textPrimary }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Main Screen
// ============================================================

export default function UnitGoalDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { width } = useWindowDimensions();
  const { horizontalCardWidth } = useResponsive();
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, canComment, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  // DTS 2.2: only Strategy Team (hrcpms.soft_delete) can soft-delete.
  // Section Head can edit unit goals but not delete them.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateUnit } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? '#374151' : '#F3F4F6',
    sectionBg: isDark ? '#374151' : '#F1F5F9',
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unitGoal, setUnitGoal] = useState<UnitGoal | null>(null);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const canApproveDeletion = (user?.is_superuser || user?.position_type === 'MANAGER' || user?.position_type === 'DIRECTOR' || (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false));

  // Comments state
  const [apiComments, setApiComments] = useState<ApiUnitGoalComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);

  const isSmallScreen = width < 768;

  // Convert API comment to display format (unified with ServiceDetailScreen pattern)
  const convertApiComment = (apiComment: ApiUnitGoalComment): CommentData => {
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ');
    const firstInitial = nameParts[0]?.[0] || '';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || '' : '';
    const initials = `${firstInitial}${lastInitial}`.toUpperCase() || 'U';

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;

    return {
      id: apiComment.id,
      author: fullName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor: getAvatarColorFromUserType(userType),
      showMenu: isOwner,
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  // Fetch unit goal comments
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const commentsData = await unitGoalCommentsApi.getForUnitGoal(id);
      setApiComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch the unit goal (KPIs included in response)
      const goalData = await unitGoalsApi.get(id);
      setUnitGoal(goalData);

      // Use KPIs from main response instead of separate API call
      setKpis(goalData.kpis || []);

      // Fetch comments for this unit goal
      fetchComments();
    } catch (error) {
      console.error('Error fetching unit goal:', error);
      showError(t('employees.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, showError, fetchComments]);

  // Close menu on outside click/scroll
  useEffect(() => {
    if (!showMenu) return;
    const handleClose = () => setShowMenu(false);
    document.addEventListener('mousedown', handleClose);
    window.addEventListener('scroll', handleClose, true);
    return () => {
      document.removeEventListener('mousedown', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, [showMenu]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleEdit = () => {
    setShowMenu(false);
    navigation.navigate('AddUnitGoal', { unitGoalId: id });
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await unitGoalsApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('unitGoals.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('unitGoals.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  // Comment handlers - unified with ServiceDetailScreen pattern
  const handleAddComment = async (text: string) => {
    try {
      await unitGoalCommentsApi.create({
        unit_goal: id,
        content: text,
        comment_type: 'note',
      });
      showSuccess(t('comments.addSuccess'));
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      showError(t('comments.addFailed'));
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (!editingComment) return;
    try {
      await unitGoalCommentsApi.update(editingComment.id, { content: text });
      showSuccess(t('comments.updateSuccess'));
      setEditingComment(null);
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      showError(t('comments.updateFailed'));
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await unitGoalCommentsApi.delete(commentId);
      showSuccess(t('comments.deleteSuccess'));
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      showError(t('comments.deleteFailed'));
    }
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  const handleCommentCancel = () => {
    setEditingComment(null);
    setShowAddComment(false);
  };

  // Use kpi_distribution from API response instead of local calculation
  const distribution = unitGoal?.kpi_distribution ?? { excellent: 0, good: 0, needs_improvement: 0, total: 0 };
  const totalKpis = (distribution.total ?? kpis.length) || 1;
  const excellentPercent = distribution.excellent ?? 0;
  const goodPercent = distribution.good ?? 0;
  const needsImprovementPercent = distribution.needs_improvement ?? 0;

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  if (!unitGoal) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>{t('goals.notFound')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, isSmallScreen && { padding: 16 }]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('unitGoals.title'), route: 'UnitGoals' },
            { label: t('unitGoals.unitGoalDetails') },
          ]}
        />

        {/* Header Card */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          {/* Edit/Menu Button */}
          <View style={styles.headerActions}>
            {/* Hide menu for view-only users (strategy_team, executive, org_executive) */}
            {!isViewOnly && (
              <>
                <TouchableOpacity
                  ref={menuButtonRef}
                  style={[styles.menuButton, { backgroundColor: themeColors.sectionBg }]}
                  onPress={() => {
                    if (!showMenu) {
                      const node = menuButtonRef.current as any;
                      if (node && node.getBoundingClientRect) {
                        const rect = node.getBoundingClientRect();
                        setMenuPosition({ top: rect.bottom + 4, left: rect.left });
                      }
                    }
                    setShowMenu(!showMenu);
                  }}
                >
                  <MoreVerticalIcon size={20} color={themeColors.textMuted} />
                </TouchableOpacity>

                {/* Dropdown Menu - Portal */}
                {showMenu && ReactDOM.createPortal(
                  <View
                    // @ts-ignore
                    onMouseDown={(e: any) => e.stopPropagation()}
                    style={[styles.menuDropdown, {
                      position: 'fixed' as any,
                      top: menuPosition.top,
                      left: menuPosition.left,
                      backgroundColor: themeColors.card,
                      borderColor: themeColors.cardBorder,
                    }]}
                  >
                    <TouchableOpacity
                      style={[styles.menuItem, { borderBottomColor: themeColors.border }]}
                      onPress={handleEdit}
                    >
                      <Text style={[styles.menuItemText, { color: themeColors.textPrimary }]}>{t('common.edit')}</Text>
                      <EditIcon size={16} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                    {/* Delete — Strategy Team / superuser only per DTS 2.2 */}
                    {canSoftDelete && (
                      <TouchableOpacity
                        style={[styles.menuItem, { borderBottomWidth: 0 }]}
                        onPress={() => {
                          setShowMenu(false);
                          handleDelete();
                        }}
                      >
                        <Text style={[styles.menuItemText, { color: '#EF4444' }]}>
                          {t('common.delete')}
                        </Text>
                        <TrashIcon size={16} color="#EF4444" />
                      </TouchableOpacity>
                    )}
                  </View>,
                  document.body
                )}
              </>
            )}
          </View>

          {/* Title */}
          <Text style={[styles.headerTitle, { color: themeColors.textPrimary }]}>{translate(unitGoal.name)}</Text>

          {/* Description */}
          {unitGoal.description && (
            <Text style={[styles.headerDescription, { color: themeColors.textMuted }]}>{translate(unitGoal.description)}</Text>
          )}
        </View>

        {/* Pending Deletion Banner */}
        {(unitGoal as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(unitGoal as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (unitGoal as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => {
              showSuccess(t('deletion.approved'));
              navigation.goBack();
            }}
            onRejected={() => {
              showSuccess(t('deletion.rejected'));
              fetchData();
            }}
            onCancelled={() => {
              showSuccess(t('deletion.requestCancelled'));
              fetchData();
            }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Info Cards Row - RTL order: Basic Info (right), KPIs (middle), Distribution (left) */}
        <View style={[styles.infoCardsRow, isSmallScreen && styles.infoCardsRowMobile]}>
          {/* KPI Status Distribution Card - appears on LEFT in RTL */}
          <View style={[styles.infoCard, styles.infoCardDistribution, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.infoCardMobile]}>
            <Text style={[styles.infoCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={styles.distributionContent}>
              {/* Donut Chart placeholder - left side */}
              <View style={styles.donutChartContainer}>
                <Svg width={140} height={140} viewBox="0 0 140 140">
                  {/* Background circle */}
                  <Circle cx="70" cy="70" r="50" fill="transparent" stroke={isDark ? '#374151' : '#E5E7EB'} strokeWidth="20" />
                  {/* Green segment - Excellent */}
                  {excellentPercent > 0 && (
                    <Circle
                      cx="70"
                      cy="70"
                      r="50"
                      fill="transparent"
                      stroke="#2CA02C"
                      strokeWidth="20"
                      strokeDasharray={`${excellentPercent * 3.14} ${314 - excellentPercent * 3.14}`}
                      strokeDashoffset="78.5"
                      transform="rotate(-90 70 70)"
                    />
                  )}
                  {/* Orange segment - Good */}
                  {goodPercent > 0 && (
                    <Circle
                      cx="70"
                      cy="70"
                      r="50"
                      fill="transparent"
                      stroke="#FF7F0E"
                      strokeWidth="20"
                      strokeDasharray={`${goodPercent * 3.14} ${314 - goodPercent * 3.14}`}
                      strokeDashoffset={78.5 - excellentPercent * 3.14}
                      transform="rotate(-90 70 70)"
                    />
                  )}
                  {/* Red segment - Needs Improvement */}
                  {needsImprovementPercent > 0 && (
                    <Circle
                      cx="70"
                      cy="70"
                      r="50"
                      fill="transparent"
                      stroke="#D62728"
                      strokeWidth="20"
                      strokeDasharray={`${needsImprovementPercent * 3.14} ${314 - needsImprovementPercent * 3.14}`}
                      strokeDashoffset={78.5 - (excellentPercent + goodPercent) * 3.14}
                      transform="rotate(-90 70 70)"
                    />
                  )}
                </Svg>
              </View>
              {/* Legend - right side */}
              <View style={styles.statusList}>
                <View style={styles.statusItem}>
                  <Text style={[styles.statusPercent, { color: themeColors.textPrimary }]}>% {excellentPercent}</Text>
                  <View style={styles.statusLabelContainer}>
                    <Text style={[styles.statusLabel, { color: themeColors.textMuted }]}>{t('dashboard.excellent')}</Text>
                    <View style={[styles.statusBar, { backgroundColor: '#2CA02C' }]} />
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={[styles.statusPercent, { color: themeColors.textPrimary }]}>% {goodPercent}</Text>
                  <View style={styles.statusLabelContainer}>
                    <Text style={[styles.statusLabel, { color: themeColors.textMuted }]}>{t('goals.good')}</Text>
                    <View style={[styles.statusBar, { backgroundColor: '#FF7F0E' }]} />
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={[styles.statusPercent, { color: themeColors.textPrimary }]}>% {needsImprovementPercent}</Text>
                  <View style={styles.statusLabelContainer}>
                    <Text style={[styles.statusLabel, { color: themeColors.textMuted }]}>{t('goals.needsImprovement')}</Text>
                    <View style={[styles.statusBar, { backgroundColor: '#D62728' }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* KPIs Progress Card - appears in MIDDLE in RTL */}
          <View style={[styles.infoCard, styles.infoCardKpi, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && [styles.infoCardMobile, styles.infoCardKpiMobile]]}>
            <Text style={[styles.infoCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
            <View style={styles.kpiProgressContainer}>
              {unitGoal.achievement_percentage != null ? (
                <CircularProgress percentage={unitGoal.achievement_percentage} />
              ) : (
                <View style={{ justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>
          </View>

          {/* Basic Information Card - appears on RIGHT in RTL */}
          <View style={[styles.infoCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.infoCardMobile]}>
            <Text style={[styles.infoCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.basicInformation')}</Text>
            <View style={styles.infoItemsContainer}>
              <View style={[styles.infoItem, { backgroundColor: themeColors.sectionBg }]}>
                <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('unitGoals.organizationalUnit')}</Text>
                <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{translateUnit(unitGoal.unit_name)}</Text>
              </View>
              {unitGoal.strategic_goal_title && (
                <View style={[styles.infoItem, { backgroundColor: themeColors.sectionBg }]}>
                  <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('unitGoals.linkedStrategicGoal')}</Text>
                  <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{translate(unitGoal.strategic_goal_title)}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Bottom Section - RTL: Comments (left), Goal Linkages (right) */}
        <View style={[styles.bottomSection, isSmallScreen && styles.bottomSectionMobile]}>
          {/* Comments Section - appears on LEFT in RTL - Unified with ServiceDetailScreen */}
          <View style={[styles.commentsColumn, isSmallScreen && styles.commentsColumnSmall]}>
            <CommentSection
              loading={commentsLoading}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              hideAddButton={!canComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCommentCancel}
            />
          </View>

          {/* Goal Linkages - appears on RIGHT in RTL (takes 2 columns) */}
          <View style={[styles.linkagesCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.linkagesCardMobile]}>
            <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('unitGoals.linkedKPIs')}</Text>

            {/* KPIs Section with gray background */}
            <View style={[styles.kpisSection, { backgroundColor: themeColors.sectionBg }]}>
              <View style={styles.kpisSectionHeader}>
                {/* Add button on LEFT in RTL - hidden for view-only users */}
                {!isViewOnly && (
                  <TouchableOpacity
                    style={[styles.addKpiButton, { backgroundColor: themeColors.card, borderColor: themeColors.border }]}
                    onPress={() => navigation.navigate('AddUnitGoal', {
                      unitGoalId: id,
                      scrollToKpi: true,
                    })}
                  >
                    <Text style={[styles.addKpiButtonText, { color: themeColors.textPrimary }]}>{t('kpis.addIndicator')}</Text>
                    <PlusIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                {/* Title on RIGHT in RTL */}
                <Text style={[styles.kpisSectionTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
              </View>

              {/* KPI Cards - Horizontal Scroll with RTL transform */}
              <View style={styles.rtlWrapper}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={styles.horizontalCardsRow}
                  style={styles.horizontalScrollContainer}
                >
                  {kpis.length === 0 ? (
                    <View style={[styles.emptyKpis, styles.rtlCard]}>
                      <Text style={styles.emptyKpisText}>{t('kpis.noKPIs')}</Text>
                    </View>
                  ) : (
                    kpis.map((kpi) => (
                      <View key={kpi.id} style={styles.rtlCard}>
                        <KPICard
                          kpi={kpi}
                          cardWidth={horizontalCardWidth}
                          onPress={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                        />
                      </View>
                    ))
                  )}
                </ScrollView>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={unitGoal?.name || ''}
        fetchImpact={async () => {
          const response = await unitGoalsApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: '#EF4444',
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#9CA3AF',
  },
  breadcrumbActive: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: COLORS.primary,
  },
  headerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 24,
    marginBottom: 24,
    position: 'relative',
  },
  headerActions: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 9999,
  },
  backButton: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  backButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#414042',
    fontWeight: '600',
  },
  menuButton: {
    padding: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
  },
  menuDropdown: {
    position: 'absolute',
    top: 44,
    left: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    minWidth: 140,
    zIndex: 99999,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  menuItemText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#374151',
  },
  headerTitle: {
    fontFamily: FONTS.medium,
    fontSize: 20,
    color: '#1F2937',
    textAlign: 'right',
    marginBottom: 8,
    fontWeight: '700',
    paddingLeft: 60,
  },
  headerDescription: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'right',
    lineHeight: 22,
  },
  infoCardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginBottom: 24,
  },
  infoCardsRowMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  infoCard: {
    flex: 1,
    minWidth: 250,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  infoCardMobile: {
    flex: 'none' as any,
    minWidth: 'auto' as any,
    width: '100%',
  },
  infoCardDistribution: {
    flex: 2,
    minWidth: 280,
  },
  infoCardKpi: {
    flex: 1,
    minWidth: 180,
    maxWidth: 300,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  infoCardKpiMobile: {
    maxWidth: '100%',
    width: '100%',
  },
  infoCardTitle: {
    fontFamily: FONTS.medium,
    fontSize: 16,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
    lineHeight: 24,
    width: '100%',
  },
  infoItemsContainer: {
    gap: 24,
  },
  infoItem: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 8,
  },
  infoLabel: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#44546A',
    textAlign: 'right',
    lineHeight: 20,
  },
  infoValue: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 24,
  },
  kpiProgressContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 10,
  },
  progressDotContainer: {
    position: 'absolute',
    top: 0,
    left: '50%',
    marginLeft: -7.5,
  },
  progressDot: {
    width: 15,
    height: 15,
    borderRadius: 7.5,
    backgroundColor: '#2CA02C',
    borderWidth: 2,
    borderColor: '#FCFCFC',
  },
  progressTextContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressPercentage: {
    fontFamily: FONTS.numbers,
    fontSize: 20,
    color: '#414042',
    fontWeight: '700',
  },
  distributionContent: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 40,
  },
  donutChartContainer: {
    width: 140,
    height: 140,
    marginRight: 20,
  },
  statusList: {
    gap: 24,
    justifyContent: 'center',
  },
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  statusLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    width: 80,
  },
  statusPercent: {
    fontFamily: FONTS.numbers,
    fontSize: 20,
    color: '#303030',
    fontWeight: '600',
  },
  statusLabel: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  statusBar: {
    width: 8,
    height: 32,
    borderRadius: 2,
  },
  bottomSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  bottomSectionMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  linkagesCard: {
    flex: 2,
    minWidth: 300,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  linkagesCardMobile: {
    flex: 'none' as any,
    minWidth: 'auto' as any,
    width: '100%',
  },
  // Comments Column - Unified with ServiceDetailScreen
  commentsColumn: {
    flex: 1,
  },
  commentsColumnSmall: {
    flex: 0,
  },
  sectionTitle: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
    lineHeight: 20,
  },
  kpisSection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    gap: 20,
  },
  kpisSectionTitle: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiButtonText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'center',
  },
  emptyKpis: {
    padding: 32,
    alignItems: 'center',
  },
  emptyKpisText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#9CA3AF',
  },
  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 12,
  },
  kpiCard: {
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
  },
  kpiCardTitle: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  kpiCardStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  kpiValueBadge: {
    backgroundColor: '#F1F5F9',
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  kpiValueText: {
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    fontSize: 14,
    color: '#414042',
    textAlign: 'center',
  },
  kpiCardProgress: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0,
  },
  kpiProgressBarBg: {
    flex: 1,
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    overflow: 'visible',
  },
  kpiProgressBarFill: {
    height: '100%',
    borderRadius: 8,
  },
  kpiProgressDotOuter: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: '#2CA02C',
    backgroundColor: '#FCFCFC',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -5,
    zIndex: 2,
  },
  kpiProgressDotInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#2CA02C',
  },
  kpiCardLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
    borderRadius: 4,
  },
  kpiCardLinkText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#414042',
    textAlign: 'right',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontFamily: FONTS.medium,
    fontSize: 18,
    color: '#1F2937',
    textAlign: 'right',
    marginBottom: 12,
    fontWeight: '600',
  },
  modalMessage: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'right',
    marginBottom: 24,
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#6B7280',
  },
  modalDeleteButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalDeleteButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#FFFFFF',
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalRejectButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#FFFFFF',
  },
  rejectionReasonInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginVertical: 16,
    minHeight: 100,
    fontFamily: FONTS.regular,
    fontSize: 14,
    textAlignVertical: 'top',
  },
});
